import {useCallback, useEffect, useState} from 'react'
import {Button, Card, DatePicker, Input, Select, Space, Table, Tag, Typography, message, type TableProps} from 'antd'
import {FileExcelOutlined, FilePdfOutlined} from '@ant-design/icons'
import {api, getErrorMessage} from '../api/client'
import {useAuth} from '../auth/AuthContext'
import {emptyLookups, type Lookups} from '../components/CaseFilters'
import {StatusTag} from '../components/StatusTag'
import {TableFilterHeader} from '../components/TableFilterHeader'
import {PageTitle} from './DashboardPage'
import type {InvestigationCase} from '../types'

const statusOptions = ['NEW', 'ASSIGNED', 'IN_PROGRESS', 'PENDING', 'COMPLETED', 'CLOSED'].map(value => ({value, label: value.replace('_', ' ')}))
const entityOptions = (items: {id: number; name: string}[]) => items.map(({id, name}) => ({value: id, label: name}))

export const ReportsPage = () => {
  const [rows, setRows] = useState<InvestigationCase[]>([])
  const [lookups, setLookups] = useState<Lookups>(emptyLookups)
  const [filters, setFilters] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(false)
  const [messageApi, contextHolder] = message.useMessage()
  const {hasPermission} = useAuth()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows((await api.get('/reports', {params: filters})).data.data)
    } catch (error) {
      messageApi.error(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void api.get('/cases/lookups/options')
      .then(response => setLookups(response.data.data))
      .catch(error => messageApi.error(getErrorMessage(error)))
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 350)
    return () => window.clearTimeout(timer)
  }, [load])

  const setFilter = (key: string, value: unknown) => {
    setFilters(current => {
      const next = {...current}
      if (value === undefined || value === null || value === '') delete next[key]
      else next[key] = value
      return next
    })
  }

  const textFilter = (label: string, key: string) => (
    <TableFilterHeader label={label}>
      <Input size="small" allowClear placeholder="Filter" value={filters[key] as string | undefined} onChange={event => setFilter(key, event.target.value)} />
    </TableFilterHeader>
  )

  const selectFilter = (label: string, key: string, options: {value: string | number; label: string}[]) => (
    <TableFilterHeader label={label}>
      <Select size="small" allowClear showSearch optionFilterProp="label" value={filters[key] as string | number | undefined} options={options} onChange={value => setFilter(key, value)} />
    </TableFilterHeader>
  )

  const setDateRange = (dates: Parameters<NonNullable<React.ComponentProps<typeof DatePicker.RangePicker>['onChange']>>[0]) => {
    setFilters(current => {
      const next = {...current}
      if (dates?.[0]) next.dateFrom = dates[0].format('YYYY-MM-DD')
      else delete next.dateFrom
      if (dates?.[1]) next.dateTo = dates[1].format('YYYY-MM-DD')
      else delete next.dateTo
      return next
    })
  }

  const download = async (format: 'excel' | 'pdf') => {
    try {
      const response = await api.get('/reports/' + format, {params: filters, responseType: 'blob'})
      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = 'eagleeye-case-report.' + (format === 'excel' ? 'xlsx' : 'pdf')
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      messageApi.error(getErrorMessage(error))
    }
  }

  const columns: TableProps<InvestigationCase>['columns'] = [
    {title: textFilter('Case No', 'caseNo'), dataIndex: 'caseNo', fixed: 'left', width: 145},
    {title: textFilter('Patient', 'patientName'), dataIndex: 'patientName', width: 165},
    {title: selectFilter('Insurance Agency', 'insuranceAgencyId', entityOptions(lookups.insuranceAgencies)), render: (_, row) => row.insuranceAgency?.name, width: 185},
    {title: selectFilter('Hospital', 'hospitalId', entityOptions(lookups.hospitals)), render: (_, row) => row.hospital?.name, width: 175},
    {title: selectFilter('Region', 'regionId', entityOptions(lookups.regions)), render: (_, row) => row.region?.name, width: 145},
    {title: selectFilter('Type', 'caseType', [{value: 'FULL', label: 'Full'}, {value: 'HALF', label: 'Half'}]), dataIndex: 'caseType', render: value => <Tag>{value}</Tag>, width: 125},
    {title: <TableFilterHeader label="Assigned Date"><DatePicker.RangePicker size="small" onChange={setDateRange} /></TableFilterHeader>, dataIndex: 'assignedDate', width: 235},
    {title: selectFilter('Hospital Staff', 'hospitalAssignedTo', entityOptions(lookups.staff)), render: (_, row) => row.hospitalAssignee?.name ?? '—', width: 165},
    {title: selectFilter('Hospital Status', 'hospitalStatus', statusOptions), dataIndex: 'hospitalStatus', render: value => <StatusTag status={value} />, width: 165},
    {title: selectFilter('Insured Staff', 'insuredAssignedTo', entityOptions(lookups.staff)), render: (_, row) => row.insuredAssignee?.name ?? '—', width: 165},
    {title: selectFilter('Insured Status', 'insuredStatus', statusOptions), dataIndex: 'insuredStatus', render: value => <StatusTag status={value} />, width: 165},
    {title: selectFilter('Overall Status', 'overallStatus', statusOptions), dataIndex: 'overallStatus', render: value => <StatusTag status={value} />, width: 165},
    {title: <TableFilterHeader label="Fees" />, dataIndex: 'fees', width: 90},
    {title: <TableFilterHeader label="TA" />, dataIndex: 'ta', width: 80},
    {title: <TableFilterHeader label="MRD" />, dataIndex: 'mrd', width: 80},
    {title: <TableFilterHeader label="Total" />, render: (_, row) => (Number(row.fees) + Number(row.ta) + Number(row.mrd)).toFixed(2), width: 95},
  ]

  const actions = (
    <Space>
      {hasPermission('reports.excel') && <Button icon={<FileExcelOutlined />} onClick={() => void download('excel')}>Download Excel</Button>}
      {hasPermission('reports.pdf') && <Button icon={<FilePdfOutlined />} onClick={() => void download('pdf')}>Download PDF</Button>}
    </Space>
  )

  return (
    <>
      {contextHolder}
      <PageTitle title="Case Report" subtitle="Filter operational and financial case data." actions={actions} />
      <Card className="section-card">
        <div className="table-summary"><Typography.Text strong>{rows.length.toLocaleString()} total cases</Typography.Text></div>
        <Table
          rowKey="id"
          loading={loading}
          bordered
          dataSource={rows}
          columns={columns}
          scroll={{x: 2100}}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) => range[0] + '–' + range[1] + ' of ' + total,
          }}
        />
      </Card>
    </>
  )
}
